import React, { useState, useRef, useEffect } from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Table,
  Typography,
  Empty,
} from "antd";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  SearchOutlined,
  LoadingOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import CustomSimpleReactValidator from "../../../components/CustomSimpleReactValidator";
import { get_language, translate } from "../../../locale";
import DashServices from "../../../services/DashServices";
import UserServices from "../../../services/UserServices";

import { TableHelper } from "../../../helpers/TableHelper";

const { Text, Title } = Typography;

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const Buscar = () => {
  const params = useParams();
  const [values, setValues] = useState({
    subscription_id: "",
    medicine: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [subscriptions, setSubscriptions] = useState([]);
  const [, forceUpdate] = useState();

  const simpleValidator = useRef(
    CustomSimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
    })
  );

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const [total, setTotal] = useState(0);

  const disabled = () => !simpleValidator.current.allValid();

  const navigate = useNavigate();

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      className: "w-10",
      render: (_, record) => (
        <Button
          onClick={(e) => {
            handleClickNuevoConsumo(record);
          }}
          icon={<ArrowRightOutlined />}
        />
      ),
    },
    {
      title: translate("Producto"),
      dataIndex: "product",
      key: "product",
      sorter: (a, b) => {
        if (a.product < b.product) return -1;
        if (a.product > b.product) return 1;
        return 0;
      },
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <div dangerouslySetInnerHTML={{ __html: record.description }}></div>
      ),
      sorter: (a, b) => {
        if (a.description < b.description) return -1;
        if (a.description > b.description) return 1;
        return 0;
      },
    },
  ];

  useEffect(() => {
    if (params.subscriptionId === undefined) {
      getSubscriptions();
    }
  }, [params]);

  useEffect(() => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    loadData(data);
  }, [JSON.stringify(tableParams)]);

  const getSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await UserServices.getSubscription(params.userName);
      if (response.status >= 200 && response.status < 300) {
        setSubscriptions(
          response.data.subscriptions.map((i) => ({
            value: i.id,
            label: i.name,
          }))
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const onFinish = (e) => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: 1,
    };
    setTableParams((v) => ({
      ...v,
      pagination: {
        ...v.pagination,
        current: 1,
      },
    }));
    loadData(data);
  };

  const loadData = async (data) => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      return;
    }
    const url = decodeURIComponent(params.fromUrl);
    if (
      (params.subscriptionId === null || params.subscriptionId === undefined) &&
      values.subscription_id === ""
    ) {
      setError("Falta el Id de suscripción.");
      return;
    }

    try {
      const res = await DashServices.searchMedicine(
        values.medicine,
        params.subscriptionId || values.subscription_id,
        data
      );

      if (res && res.status >= 200 && res.status < 300 && res.data) {
        setTotal(
          Math.ceil(
            res.data.total_medicines / tableParams.pagination.pageSize
          ) * tableParams.pagination.pageSize
        );
        setData(
          res.data.medicines.map((i, k) => {
            let description = `<strong>${translate("Descripción")}:</strong> `;
            if (i.description !== undefined && i.description !== null) {
              description += i.description;
            }
            description += `<br /><strong>${translate("Monodroga")}:</strong> `;
            if (i.monodrug !== undefined && i.monodrug !== null) {
              description += i.monodrug;
            }
            description += `<br /><strong>${translate(
              "Presentación"
            )}:</strong> `;
            if (i.presentation !== undefined && i.presentation !== null) {
              description += i.presentation;
            }
            description += `<br /><strong>${translate("Poder")}:</strong> `;
            if (i.power !== undefined && i.power !== null) {
              description += i.power;
            }
            return {
              ...i,
              key: k,
              product: `${
                i.commercial_brand === "" || i.commercial_brand === null
                  ? ""
                  : i.commercial_brand + " "
              }(${i.name})`,
              description: description,
              date_from: i.date_from,
              date_to: i.date_to,
              status: i.status,
              indication: i.indication,
            };
          })
          /* .map((i, k) => ({
              ...i,
              key: k,
              actions: (
                <Button
                  onClick={(e) => {
                    handleClickNuevoConsumo(i);
                  }}
                  icon={<ArrowRightOutlined />}
                ></Button>
              ),
            })) */
        );
        setLoading(false);
      } else {
        setError("Error al buscar el medicamento.");
      }
      setLoading(false);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBack = (e) => {
    navigate(-1);
  };

  const handleClickNuevoConsumo = (medicine) => {
    console.log("medicine", medicine);
    localStorage.setItem("medicine", JSON.stringify(medicine));
    navigate(
      `/admin/medicamentos/nuevo-consumo/${medicine.id}/${params.userName}`
    );
  };

  return (
    <div className="flex flex-col min-h-full w-screen max-w-7xl pb-16 m-10">
      <Row gutter={16} className="mb-10">
        {params.fromUrl.indexOf("buscar-paciente") !== -1 && (
          <Col className="gutter-row" span="24">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/admin">{translate("Inicio")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to={`/admin/buscar-paciente/${params.userName}`}>
                  {translate("Resumen individual")}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link
                  to={`/admin/buscar-paciente/${params.userName}/medicinas-paciente`}
                >
                  {translate("Medicamentos del Paciente")}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{translate("Buscar")}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        )}
        <Col span="24" className="my-4">
          <Title level={2} className="text-center" style={{ marginBottom: 0 }}>
            {translate("Buscar medicamento")}
          </Title>
          <p className="text-center">
            <Text type="secondary">
              (
              {translate(
                "Tenga en cuenta que solo podrá buscar medicamentos del país de origen del paciente"
              )}
              )
            </Text>
          </p>
        </Col>
        <Col span={24}>
          <Form onFinish={onFinish} autoComplete="off">
            {error && (
              <Alert
                message={error}
                type="error"
                className="mb-6"
                closable
                onClose={() => setError("")}
              />
            )}
            <Row gutter={16} className="w-full">
              {subscriptions.length > 0 && (
                <Col span={9}>
                  <Select
                    name="subscription_id"
                    onChange={(value) => {
                      setValues((v) => ({ ...v, subscription_id: value }));
                    }}
                    value={values.subscription_id}
                    className="w-full"
                    disabled={params.subscriptionId !== undefined}
                  >
                    <Select.Option value="">
                      Seleccione suscripción
                    </Select.Option>
                    {subscriptions.map((item, key) => (
                      <Select.Option key={key} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {simpleValidator.current.message(
                    "subscription_id",
                    values.subscription_id,
                    "required"
                  )}
                </Col>
              )}
              <Col span={9}>
                <Input
                  placeholder={translate("Monodroga o Marca Comercial")}
                  className="w-full"
                  name="medicine"
                  onChange={handleChange}
                  // onBlur={handleBlur}
                />
                {simpleValidator.current.message(
                  "medicine",
                  values.medicine,
                  "required|min:3",
                  get_language === "es"
                    ? {
                        messages: {
                          required:
                            "El campo Monodroga o Marca Comercial es requerido",
                          min: "El campo Monodroga o Marca Comercial debe tener al menos 3 caracteres",
                        },
                      }
                    : {}
                )}
              </Col>
              <Col>
                <Row gutter={16}>
                  <Col>
                    <Button
                      htmlType="submit"
                      icon={
                        loading ? (
                          <LoadingOutlined />
                        ) : (
                          <SearchOutlined style={{ position: "relative" }} />
                        )
                      }
                      type="primary"
                      className="bg-custom_primary"
                      disabled={disabled()}
                    >
                      {translate("Buscar")}
                    </Button>
                  </Col>
                  {/* <Col>
                    <Button icon={<ReloadOutlined />} onClick={handleBack}>
                      {translate("Volver")}
                    </Button>
                  </Col> */}
                </Row>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col className="gutter-row mb-0">
          <Title type="secondary" level={3} className="mb-0">
            {translate("Resultado")}
          </Title>
        </Col>
        <Col className="w-full">
          <Table
            dataSource={data}
            columns={columns}
            size="small"
            loading={
              loading && { indicator: antIcon, className: "text-custom_blue" }
            }
            rowKey={(i) => i.id}
            locale={{
              emptyText: <Empty description={translate("No hay datos")} />,
              sortTitle: translate("Ordenar"),
              triggerDesc: translate("Ordenar (descendente)"),
              triggerAsc: translate("Ordenar (ascendente)"),
              cancelSort: translate("Cancelar orden"),
            }}
            pagination={{ ...tableParams.pagination, total: total }}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Buscar;
